import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { lastValueFrom, Observable } from "rxjs";
import { ClassificationSystem } from "../Model/classification/ClassificationSystem";
import { ClassificationClass } from "../Model/classification/ClassificationClass";
import { AddClassification } from '../Model/classification/AddClassification';
import { FeatureSystem } from '../Model/Catalog/FeatureModels/FeatureSystem';
import { FeatureValue } from '../Model/classification/FeatureValue';
import { StringResponse } from "../Model/Dto/StringResponse";
import { LoginService } from "./login.service";
import { CatalogService } from "./CatalogManagement/catalog.service";
import { ClassificationFeature } from "../Model/classification/ClassificationFeature";


@Injectable()
export class ClassificationService {
  constructor(private http: HttpClient, private loginService: LoginService, private catalogService: CatalogService) {

  }

  getClassificationSystemsForCustomer(customerId: string, language: string): Observable<ClassificationSystem[]>  {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      params: new HttpParams().append("customerId", customerId).append("language", language)
    }
    return this.http.get<ClassificationSystem[]>("api/Classification/GetClassificationSystemsForCustomer", options); 
  }

  createClassificationSystem(model: ClassificationSystem): Promise<ClassificationSystem> {
    return lastValueFrom(this.http.post<ClassificationSystem>("api/Classification/UpdateClassificationSystem", model));
  }

  duplicateClassificationSystem(model: ClassificationSystem): Promise<ClassificationSystem> {
    return lastValueFrom(this.http.post<ClassificationSystem>("api/Classification/DuplicateClassificationSystem", model));
  }

  deleteClassificationSystem(model: ClassificationSystem): Promise<StringResponse> {
    return lastValueFrom(this.http.post<StringResponse>("api/Classification/DeleteClassificationSystem", model));
  }

  getClassificationSystemsForEditor(): Promise<ClassificationSystem[]> {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      params: new HttpParams()
    }
    return lastValueFrom(this.http.get<ClassificationSystem[]>("api/classification/GetClassificationSystemsForEditor", options));
  }

  getClassificationClasses(classificationSystemId: string): Promise<ClassificationClass[]> {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      params: new HttpParams().append("classificationSystemId", classificationSystemId)
    }
    return lastValueFrom(this.http.get<ClassificationClass[]>("api/Classification/GetClassificationClasses", options));
  }

  getClassificationClassesWithoutFeatures(classificationSystemId: string): Promise<ClassificationClass[]> {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      params: new HttpParams().append("classificationSystemId", classificationSystemId)
    }
    return lastValueFrom(this.http.get<ClassificationClass[]>("api/Classification/getClassificationClassesWithoutFeatures", options));
  }

  getClassificationClassFeatures(classificationClassId: string): Promise<ClassificationFeature[]> {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      params: new HttpParams().append("classificationClassId", classificationClassId)
    }
    return lastValueFrom(this.http.get<ClassificationFeature[]>("api/Classification/GetClassificationClassFeatures", options));
  }

  addClassificationClass(classificationClass: ClassificationClass): Promise<ClassificationClass> {
    return lastValueFrom(this.http.post<ClassificationClass>("api/Classification/AddClassificationClass", classificationClass));
  }

  duplicateClassificationClass(classificationClass: ClassificationClass): Promise<ClassificationClass> {
    return lastValueFrom(this.http.post<ClassificationClass>("api/Classification/DuplicateClassificationClass", classificationClass));
  }

  deleteClassificationClass(classificationClass: ClassificationClass): Promise<StringResponse> {
    return lastValueFrom(this.http.post<StringResponse>("api/Classification/DeleteClassificationClass", classificationClass));
  }

  getClass(id: string): Observable<ClassificationClass> {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      params: new HttpParams().append("id", id)
    }
    return this.http.get<ClassificationClass>("api/Classification/GetClassificationClass", options);
  }

  updateClassificationSystem(selectedSystem: ClassificationSystem): Promise<ClassificationSystem> {
    return lastValueFrom(this.http.post("api/Classification/UpdateClassificationSystem", selectedSystem));
  }

  updateClassificationClass(selectedClass: ClassificationClass): Promise<ClassificationClass> {
    return lastValueFrom(this.http.post("api/Classification/UpdateClassificationClass", selectedClass));
  }

  addClassificationFeatures(model: AddClassification): Promise<FeatureSystem[]> {
    return lastValueFrom(this.http.post<FeatureSystem[]>("api/Classification/AddClassificationFeatures", model));
  }

  getFeatureValues(featureName, classificationsSystemID, referenceFeatureGroupId): Observable<FeatureValue[]> {
    const options = {
      headers: new HttpHeaders().append("Content-Type", "application/json"),
      params: new HttpParams().append("featureName", featureName)
        .append("classificationsSystemID", classificationsSystemID)
        .append("referenceFeatureGroupId", referenceFeatureGroupId)
    }
    return (this.http.get<FeatureValue[]>("api/Classification/GetFeatureValues", options)) as any;
  }

  isReadOnlyForUser(classificationSystem: ClassificationSystem) {
    if (!this.loginService.isManager && !this.loginService.isAdmin)
      return true;

    return (this.isGlobalClassificationSystem(classificationSystem) ||
            !this.isUserCreatorOfClassificationSystem(classificationSystem)) &&
            !this.loginService.isAdmin;
  }

  isGlobalClassificationSystem(classificationSystem: ClassificationSystem): boolean {
    if (classificationSystem != undefined) {
      return classificationSystem.isGlobal;
    }
    return false;
  }

  isUserCreatorOfClassificationSystem(classificationSystem: ClassificationSystem): boolean {
    if (classificationSystem != undefined) {
      return classificationSystem.createdByCustomerId == this.loginService.currentUser.customerId;
    }
    return false;
  }

  isClassificationSystem(name: string) {
    var classificationSystems = this.catalogService.catalog.classificationSystems;
    if (!classificationSystems)
      return false;

    const key = classificationSystems.findIndex((classificationSystem => classificationSystem.name === name));
    return key !== -1 ? true : false;
  }
}
